/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react-hooks/exhaustive-deps */
// app/components/FAQ.tsx
"use client";

import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

const GREEN = "rgb(34 150 83)";

type FAQ = {
  id: number;
  question: string;
  answer: string;
  category: string;
};

const FAQS: FAQ[] = [
  {
    id: 1,
    question: "How often should I visit the dentist?",
    answer:
      "At Pinak Dental Clinic, we recommend visiting the dentist every 6 months for routine check-ups and professional cleaning. Regular visits help prevent dental problems and allow early detection of issues before they become serious.",
    category: "General",
  },
  {
    id: 2,
    question: "Are dental implant procedures painful?",
    answer:
      "Dental implant procedures are performed under local anesthesia, so you should not feel pain during the treatment. Mild discomfort or swelling after the procedure is normal and usually settles within a few days with prescribed medication and care.",
    category: "Treatments",
  },
  {
    id: 3,
    question: "How long does professional teeth whitening last?",
    answer:
      "Teeth whitening results can last from several months to a few years depending on your eating habits, oral hygiene, and lifestyle. Avoiding tobacco, coffee, and regular dental cleanings helps maintain brighter teeth longer.",
    category: "Cosmetic",
  },
  {
    id: 4,
    question: "Is root canal treatment safe?",
    answer:
      "Yes, root canal treatment is a commonly performed and safe procedure used to save infected or damaged teeth. Our clinic uses modern techniques and equipment to ensure comfort, safety, and predictable outcomes.",
    category: "Treatments",
  },
  {
    id: 5,
    question: "What are clear aligners and who can use them?",
    answer:
      "Clear aligners are transparent removable trays used to straighten teeth without metal braces. They are suitable for many mild to moderate alignment issues and are custom-designed after a dental evaluation.",
    category: "Cosmetic",
  },
  {
    id: 6,
    question: "Do you accept insurance or cashless treatment?",
    answer:
      "We assist patients with insurance documentation wherever applicable. Coverage depends on your insurance provider and policy terms. Our team can guide you regarding billing and payment options during your visit.",
    category: "General",
  },
  {
    id: 7,
    question: "What is the approximate cost of dental implants?",
    answer:
      "The cost of dental implants depends on the type of implant, number of teeth, bone condition, and treatment complexity. A personalized consultation is required to provide accurate pricing and treatment planning.",
    category: "Treatments",
  },
  {
    id: 8,
    question: "How can I prevent cavities and gum problems?",
    answer:
      "Brush twice daily with fluoride toothpaste, floss regularly, reduce sugary foods, and attend routine dental check-ups. Professional cleaning and preventive treatments also help maintain healthy teeth and gums.",
    category: "General",
  },
];

export default function FAQ() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "General", "Treatments", "Cosmetic"];

  const filteredFAQs = FAQS.filter((faq) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <section
      id="faqs"
      ref={rootRef}
      className="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/20 to-white py-16 sm:py-20 lg:py-24"
    >
      {/* ===== DECORATIVE BACKGROUND (HIDE ON MOBILE) ===== */}
      <div className="pointer-events-none absolute inset-0 hidden md:block opacity-10">
        <motion.div
          className="absolute left-[10%] top-32 size-48 rounded-full bg-emerald-300"
          animate={{ scale: [1, 1.2, 1], y: [0, -20, 0] }}
          transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute right-[15%] bottom-40 size-64 rounded-2xl bg-cyan-300"
          animate={{ rotate: [0, 180, 0], scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 15, ease: "easeInOut" }}
        />
        <motion.svg
          className="absolute bottom-20 left-[20%] size-40"
          viewBox="0 0 200 200"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke={GREEN}
            strokeWidth="3"
          />
        </motion.svg>
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-12">
        {/* ===== HEADER ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-12 text-center"
        >
          <motion.span
            className="inline-block rounded-full border-2 border-emerald-300 bg-emerald-100 px-5 py-2 text-sm font-bold text-emerald-700"
            whileHover={{ scale: 1.08 }}
          >
            ❓ FAQ
          </motion.span>

          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900">
            Dental Questions{" "}
            <span className="bg-gradient-to-r from-emerald-600 to-emerald-400 bg-clip-text text-transparent">
              Answered
            </span>
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-gray-600">
            Find answers to common dental care questions
          </p>
        </motion.div>

        {/* ===== SEARCH ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 sm:mb-8"
        >
          <div className="relative">
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-2xl border-2 border-emerald-200 bg-white px-5 py-3 pl-12 text-base sm:text-lg text-gray-900 placeholder-gray-500 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">
              🔍
            </span>
          </div>
        </motion.div>

        {/* ===== CATEGORIES ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-10 flex flex-wrap justify-center gap-2 sm:gap-3"
        >
          {categories.map((category) => (
            <motion.button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`rounded-full px-4 sm:px-6 py-2 text-sm font-semibold transition-all ${
                selectedCategory === category
                  ? "border-2 border-emerald-500 bg-emerald-500 text-white shadow-lg"
                  : "border-2 border-emerald-200 bg-white text-emerald-700 hover:border-emerald-400"
              }`}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        {/* ===== FAQ ACCORDION ===== */}
        <div className="space-y-3 sm:space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredFAQs.map((faq) => {
              const isExpanded = expandedId === faq.id;

              return (
                <motion.div key={faq.id} layout>
                  <motion.div
                    className="overflow-hidden rounded-2xl border-2 bg-white shadow-md"
                    style={{
                      borderColor: isExpanded
                        ? GREEN
                        : "rgba(209, 213, 219, 0.5)",
                    }}
                  >
                    <button
                      onClick={() =>
                        setExpandedId(isExpanded ? null : faq.id)
                      }
                      className="flex w-full items-start sm:items-center justify-between gap-4 p-4 sm:p-6 text-left"
                    >
                      <div className="flex items-start sm:items-center gap-4">
                        <motion.div
                          className="grid size-9 sm:size-10 flex-shrink-0 place-items-center rounded-full text-lg"
                          style={{
                            backgroundColor: isExpanded
                              ? GREEN
                              : "#d1fae5",
                          }}
                        >
                          <span
                            className={
                              isExpanded
                                ? "text-white"
                                : "text-emerald-700"
                            }
                          >
                            {isExpanded ? "−" : "+"}
                          </span>
                        </motion.div>

                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                          {faq.question}
                        </h3>
                      </div>

                      <div className="hidden sm:block rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        {faq.category}
                      </div>
                    </button>

                    <motion.div
                      animate={{
                        height: isExpanded ? "auto" : 0,
                        opacity: isExpanded ? 1 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-emerald-100 bg-emerald-50/30 px-4 sm:px-6 pb-6 pt-4">
                        <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  </motion.div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

   
      </div>
    </section>
  );
}
